<template>
    <div class="vibe-component-generator">
        <!-- Header -->
        <app-row>
            <app-col cols="12" class="d-flex align-center">
                <h3 class="section-title mb-0">
                    <v-icon left color="primary">{{ icon }}</v-icon>
                    {{ title }}
                </h3>
                <v-chip v-if="hasTemplate" small class="ml-3" color="success" outlined>
                    <v-icon left small>mdi-check</v-icon>
                    Configurado
                </v-chip>
                <v-spacer></v-spacer>
                <v-menu offset-y left max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on" title="Componentes especiales disponibles">
                            <v-icon>mdi-help-circle-outline</v-icon>
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title class="text-subtitle-1 py-2">Componentes especiales</v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="text-body-2">
                            <div class="mb-2">
                                <strong>&lt;markdown-view&gt;</strong><br>
                                <code>&lt;markdown-view :content="localData.texto"&gt;&lt;/markdown-view&gt;</code><br>
                                <span class="grey--text">Renderiza Markdown estándar</span>
                            </div>
                            <div>
                                <strong>&lt;atlassian-markup&gt;</strong><br>
                                <code>&lt;atlassian-markup :content="localData.texto"&gt;&lt;/atlassian-markup&gt;</code><br>
                                <span class="grey--text">Soporta: {color:#hex}texto{color}, *bold*, _italic_, +underline+, -strike-</span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-menu>
                <v-btn
                    v-if="hasTemplate"
                    icon
                    small
                    color="error"
                    @click="clearTemplate"
                    title="Borrar template actual"
                >
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </app-col>
        </app-row>

        <!-- Prompt unificado -->
        <app-row class="mt-4">
            <v-col cols="10">
                <v-textarea
                    :label="hasTemplate ? 'Describe los cambios que quieres hacer' : 'Describe el componente que quieres generar'"
                    :placeholder="hasTemplate ? 'Ej: Cambia el color del boton a rojo, anade un campo de fecha...' : 'Ej: Un formulario con campos para nombre y email...'"
                    outlined
                    rows="2"
                    v-model="prompt"
                    hide-details
                ></v-textarea>
            </v-col>
            <v-col cols="2" class="d-flex flex-column justify-center">
                <v-btn
                    color="primary"
                    :loading="generating"
                    @click="executePrompt"
                    block
                >
                    <v-icon left>{{ hasTemplate ? 'mdi-pencil' : 'mdi-creation' }}</v-icon>
                    {{ hasTemplate ? 'Modificar' : 'Generar' }}
                </v-btn>
                <v-checkbox
                    v-model="languageSupport"
                    label="Language support"
                    hide-details
                    dense
                    class="mt-2"
                ></v-checkbox>
            </v-col>
        </app-row>

        <!-- Codigo (colapsable) - siempre visible para permitir edicion manual -->
        <app-row class="mt-4">
            <v-col cols="12">
                <v-expansion-panels flat>
                    <v-expansion-panel>
                        <v-expansion-panel-header class="code-panel-header">
                            <div class="d-flex align-center">
                                <v-icon left small>mdi-code-tags</v-icon>
                                {{ hasTemplate ? 'Ver/Editar codigo' : 'Escribir codigo manualmente' }}
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="mt-2">
                                <div class="code-editor-label">Template HTML</div>
                                <CodeMirrorEditor
                                    :value="value"
                                    @input="$emit('input', $event)"
                                    language="html"
                                    height="400px"
                                />
                            </div>
                            <div class="mt-4">
                                <div class="code-editor-label">Component Options (JavaScript)</div>
                                <CodeMirrorEditor
                                    :value="options"
                                    @input="$emit('update:options', $event)"
                                    language="javascript"
                                    height="300px"
                                />
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </app-row>

        <!-- Preview con Tabs -->
        <v-divider class="my-4"></v-divider>
        <app-row v-if="hasTemplate">
            <v-col cols="12">
                <div class="d-flex align-center mb-2">
                    <h4 class="mb-0">
                        <v-icon left small color="primary">mdi-eye</v-icon>
                        Vista previa
                    </h4>
                    <v-btn
                        icon
                        x-small
                        class="ml-2"
                        @click="previewKey++"
                        title="Refrescar vista"
                    >
                        <v-icon small>mdi-refresh</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-switch
                        v-if="previewTab === 0"
                        v-model="narrowPreview"
                        label="Modo estrecho"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                    ></v-switch>
                </div>

                <v-tabs v-model="previewTab" class="preview-tabs">
                    <v-tab>
                        <v-icon left small>mdi-application</v-icon>
                        Dinámico
                    </v-tab>
                    <v-tab>
                        <v-icon left small>mdi-code-braces</v-icon>
                        JSON
                    </v-tab>
                    <v-tab>
                        <v-icon left small>mdi-form-select</v-icon>
                        Editor
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="previewTab" class="preview-tabs-content">
                    <!-- Tab: Vista Dinámica -->
                    <v-tab-item>
                        <div class="pa-4" :class="{ 'd-flex justify-center': narrowPreview }">
                            <div :class="{ 'narrow-preview-container': narrowPreview }">
                                <DynamicComponentRenderer
                                    :key="previewKey"
                                    :schema="previewSchema"
                                    :code="value"
                                    :options="options"
                                    :actions_catalog="actionsCatalog"
                                    :company_id="companyId"
                                    v-model="previewData"
                                    @input="onEditorInput"
                                />
                            </div>
                        </div>
                    </v-tab-item>

                    <!-- Tab: JSON -->
                    <v-tab-item>
                        <pre class="json-preview">{{ JSON.stringify(previewData, null, 2) }}</pre>
                    </v-tab-item>

                    <!-- Tab: Editor Clásico -->
                    <v-tab-item>
                        <div class="pa-4">
                            <RecursivePayloadOnSchemaEditor
                                :key="editorKey"
                                :schema="previewSchema"
                                v-model="previewData"
                                @input="onEditorInput"
                            />
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-col>
        </app-row>

        <app-row v-if="!hasTemplate">
            <v-col cols="12">
                <v-alert type="info" text outlined>
                    No hay template configurado. Usa el campo de arriba para generar uno.
                </v-alert>
            </v-col>
        </app-row>
    </div>
</template>

<script>
import AppRow from "@/components/layout/AppRow.vue";
import AppCol from "@/components/layout/AppCol.vue";
import DynamicComponentRenderer from "@/modules/entities/infra/DynamicComponentRenderer.vue";
import RecursivePayloadOnSchemaEditor from "@/modules/entities/infra/RecursivePayloadOnSchemaEditor.vue";
import CodeMirrorEditor from "@/components/CodeMirrorEditor.vue";
import { generateTextWithAI } from "@/modules/shared/infra/Utils";

export default {
    name: "VibeComponentGenerator",
    components: {
        AppRow,
        AppCol,
        DynamicComponentRenderer,
        RecursivePayloadOnSchemaEditor,
        CodeMirrorEditor
    },
    props: {
        // v-model para template HTML
        value: {
            type: String,
            default: ''
        },
        // v-model:options para component options JS
        options: {
            type: String,
            default: ''
        },
        // Titulo de la seccion
        title: {
            type: String,
            default: 'Component Generator'
        },
        // Icono mdi
        icon: {
            type: String,
            default: 'mdi-code-tags'
        },
        // Prompt base ya construido (string)
        basePrompt: {
            type: String,
            required: true
        },
        // Schema para el preview
        previewSchema: {
            type: Object,
            default: () => ({})
        },
        // Actions catalog para el preview
        actionsCatalog: {
            type: Array,
            default: () => []
        },
        // Company ID para el preview
        companyId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            prompt: '',
            generating: false,
            previewData: {},
            narrowPreview: false,
            previewTab: 0,
            previewKey: 0,
            editorKey: 0,
            languageSupport: false
        };
    },
    computed: {
        hasTemplate() {
            return !!(this.value && this.value.trim());
        }
    },
    watch: {
        previewTab(newVal, oldVal) {
            // Refrescar al cambiar de pestaña para sincronizar
            if (newVal === 0 && oldVal !== 0) {
                this.previewKey++;
            } else if (newVal === 2 && oldVal !== 2) {
                this.editorKey++;
            }
        }
    },
    methods: {
        async executePrompt() {
            if (this.hasTemplate) {
                await this.modifyGeneration();
            } else {
                await this.generate();
            }
        },

        clearTemplate() {
            this.$emit('input', '');
            this.$emit('update:options', '');
            this.prompt = '';
        },

        onEditorInput(newData) {
            this.previewData = newData;
        },

        async generate() {
            this.generating = true;
            try {
                const i18nInfo = this.languageSupport ? `
INTERNACIONALIZACION:
- Puedes acceder al idioma actual del usuario con $i18n.locale
- Los valores posibles son: 'es' (espanol), 'en' (ingles), 'cat' (catalan)
- Usa esto para mostrar textos en el idioma correcto del usuario
- Ejemplo: {{ $i18n.locale === 'es' ? 'Guardar' : $i18n.locale === 'en' ? 'Save' : 'Desar' }}
` : '';
                const fullPrompt = this.basePrompt + i18nInfo +
                    (this.prompt ? '\n\nInstrucciones adicionales del usuario:\n' + this.prompt : '');

                const output_schema = {
                    type: "object",
                    properties: {
                        template: {
                            type: "string",
                            description: "HTML template string del componente Vue (sin decoraciones, listo para Vue.compile)"
                        },
                        component_options: {
                            type: "string",
                            description: "String de JavaScript con el objeto de opciones del componente (data, computed, methods). Debe ser evaluable con new Function('return ' + component_options). NO incluyas localData en data."
                        }
                    },
                    required: ["template", "component_options"]
                };

                const response = await generateTextWithAI(fullPrompt, output_schema, false, false);
                if (response && response.response) {
                    const result = response.response;
                    this.$emit('input', result.template);
                    this.$emit('update:options', result.component_options);
                    this.prompt = '';
                }
            } catch (error) {
                console.error('Error generating template:', error);
            } finally {
                this.generating = false;
            }
        },

        async modifyGeneration() {
            if (!this.value || !this.prompt) {
                return;
            }
            this.generating = true;

            try {
                const lineCount = this.value.split('\n').length;
                const isLargeTemplate = lineCount > 200;

                let modificationGuidance = '';
                if (isLargeTemplate) {
                    modificationGuidance = `
                        IMPORTANTE - TEMPLATE GRANDE (${lineCount} lineas):
                        - Este template es extenso, se PRECISO y CUIDADOSO
                        - Si el usuario menciona lineas especificas (ej: "linea 450"), enfócate SOLO en esas lineas
                        - NO regeneres todo el template innecesariamente
                        - MANTÉN el código existente que no se relaciona con el cambio solicitado
                        - Si haces cambios, asegúrate de mantener la estructura y funcionalidad existente
                        - Si el cambio es pequeño (un color, un texto, un atributo), modifica SOLO esa parte
                    `;
                }

                const fullPrompt = `Eres un modificador de componentes Vue dinamicos. Modifica el siguiente componente Vue 2 segun las instrucciones del usuario.

TEMPLATE ACTUAL (${lineCount} lineas):
${this.value}

COMPONENT OPTIONS ACTUALES:
${this.options || '{}'}

INSTRUCCIONES DE MODIFICACION:
${this.prompt}
${modificationGuidance}
Recuerda que el componente usa:
- Vue 2 con Vuetify
- Prop "value" con v-model
- Metodo update() para emitir cambios (YA VIENE POR DEFECTO, no lo incluyas en component_options)
- Metodo run_action(action_name) para ejecutar acciones (YA VIENE POR DEFECTO, no lo incluyas en component_options)
- localData se crea automaticamente desde this.value (NO lo incluyas en component_options.data)
- En component_options.data SOLO incluye datos auxiliares adicionales (contadores, flags, estados UI, etc.)
- Puedes usar computed para propiedades calculadas
- Puedes usar methods para funciones auxiliares ADICIONALES (no update ni run_action)
${this.languageSupport ? `
INTERNACIONALIZACION:
- Puedes acceder al idioma actual del usuario con $i18n.locale
- Los valores posibles son: 'es' (espanol), 'en' (ingles), 'cat' (catalan)
- Usa esto para mostrar textos en el idioma correcto del usuario
- Ejemplo: {{ $i18n.locale === 'es' ? 'Guardar' : $i18n.locale === 'en' ? 'Save' : 'Desar' }}
` : ''}
Devuelve el template y component_options modificados COMPLETOS.`;

                const output_schema = {
                    type: "object",
                    properties: {
                        template: {
                            type: "string",
                            description: "HTML template string modificado del componente Vue"
                        },
                        component_options: {
                            type: "string",
                            description: "String de JavaScript con el objeto de opciones modificado (data, computed, methods). NO incluyas localData en data."
                        }
                    },
                    required: ["template", "component_options"]
                };

                const response = await generateTextWithAI(fullPrompt, output_schema, false, false);
                if (response && response.response) {
                    const result = response.response;
                    this.$emit('input', result.template);
                    this.$emit('update:options', result.component_options);
                    this.prompt = '';
                }
            } catch (error) {
                console.error('Error modifying template:', error);
            } finally {
                this.generating = false;
            }
        }
    }
};
</script>

<style scoped>
.vibe-component-generator {
    width: 100%;
}

.section-title {
    color: #1976d2;
    font-weight: 500;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
}

.code-panel-header {
    background-color: #f5f5f5;
    min-height: 40px !important;
    padding: 8px 16px !important;
}

.code-textarea >>> textarea {
    font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.4;
}

.narrow-preview-container {
    width: 600px;
    max-width: 100%;
    border: 1px dashed #ccc;
    border-radius: 8px;
    padding: 16px;
    background-color: #fafafa;
}

.preview-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.preview-tabs-content {
    border: 1px solid #e0e0e0;
    border-top: none;
    border-radius: 0 0 4px 4px;
    min-height: 200px;
}

.json-preview {
    background-color: #263238;
    color: #aabfc9;
    padding: 16px;
    border-radius: 0 0 4px 4px;
    font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.4;
    overflow-x: auto;
    margin: 0;
    min-height: 200px;
}

.code-editor-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
    margin-bottom: 4px;
    margin-left: 2px;
}
</style>
